// modules
import { Form } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

// hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import { useCapitaStarDelinkUtil } from "containers/CapitaStarDelinkPage/useCapitaStarDelinkUtil";
import { useVehicleIUDelinkPage } from "containers/CapitaStarDelinkPage/useVehicleIUDelinkPage";
import { useKrisFlyerDelinkPage } from "containers/KrisFlyerDelinkPage/useKrisFlyerDelinkPage";

// redux
import {
	getCardEnquiryDetails,
	getMemberEnquiryDetails,
	loadingCardEnquiryDetailsSelector,
	loadingDelinkMemberSelector,
	loadingMemberEnquiryDetailsSelector,
	resetEnquiryDetails
} from "redux/userList/userSlice";

// utils
import { validateCardNumber, validateUserID } from "utils/Validations/fieldValidations";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants
import { CapitaStarDelink as Constants, Routes } from "constants/global";

// Type definitions
import type { FormInstance } from "antd/es/form/Form";
import { useLocation } from "react-router-dom";

type InputFieldsType = {
	cardNumber?: string;
	userId?: string;
};

type FieldErrorsType = {
	cardNumber?: boolean;
	userId?: boolean;
};

type InitValueTypes = {
	cardNumber: string;
	userId: string;
	capitaStarId: string;
	vehicleIUDelink: string;
};

export const initValues: InitValueTypes = {
	cardNumber: "",
	userId: "",
	capitaStarId: "",
	vehicleIUDelink: ""
};

type CapitaStarDelinkPageTypes = {
	inputFields: InputFieldsType;
	loading: boolean;
	delinkloading: boolean;
	memberloading: boolean;
	capitaStarNumber: string;
	form: FormInstance<any>;
	initValues: InitValueTypes;
	fieldErrors: FieldErrorsType;
	isNextEnabled: boolean;
	vehicleIUNumber: string;
	KrisFlyerNumber: string;
	getFieldValue: (fieldName: string, form: FormInstance<any>) => any;
	handleCardNumChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleUserIdChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleMemberIdSearch: () => Promise<void>;
	handleSubmit: () => Promise<void>;
	handleVehicleIUSubmit: () => Promise<void>;
	handleKrisFlyerSubmit: () => Promise<void>;
};

export function useCapitaStarDelinkPage(): CapitaStarDelinkPageTypes {
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();

	const cardEnquiryLoading = useAppSelector(loadingCardEnquiryDetailsSelector);
	const memberEnquiryLoading = useAppSelector(loadingMemberEnquiryDetailsSelector);
	const delinkMemberLoading = useAppSelector(loadingDelinkMemberSelector);

	const [form] = Form.useForm();

	const [inputFields, setInputFields] = useState<InputFieldsType>({
		cardNumber: initValues.cardNumber,
		userId: initValues.userId
	});

	const [fieldErrors, setFieldErrors] = useState<FieldErrorsType>({
		cardNumber: false,
		userId: false
	});

	const { capitaStarNumber, submitUtilHandler } = useCapitaStarDelinkUtil();
	const { vehicleIUNumber, vehicleIUSubmitHandler } = useVehicleIUDelinkPage();
	const { KrisFlyerNumber, KrisFlyerSubmitHandler } = useKrisFlyerDelinkPage();

	useEffect(() => {
		form.setFieldsValue({ capitaStarId: capitaStarNumber });
		form.setFieldsValue({ vehicleIUDelink: vehicleIUNumber });
		form.setFieldsValue({ [Constants.FORM_LABELS.KRISFLYER_DELINK]: KrisFlyerNumber });
	}, [capitaStarNumber, vehicleIUNumber, KrisFlyerNumber]);

	useEffect(() => {
		form?.setFieldsValue(initValues);
		return () => {
			dispatch(resetEnquiryDetails());
		};
	}, [pathname]);

	const validateFields = useCallback(() => {
		const fieldsToValidate = [
			{ key: "cardNumber", validator: validateCardNumber },
			{ key: "userId", validator: validateUserID }
		];

		// Reset error state before validations
		setFieldErrors({
			cardNumber: false,
			userId: false
		});

		const errors: Array<{ field: string | undefined }> = [];

		fieldsToValidate.forEach((field: { key: string; validator: (args: string) => boolean }) => {
			const key = field.key as keyof FieldErrorsType;
			const value = inputFields[key];
			if (value) {
				const isValid = field.validator(value.toString());

				if (!isValid) {
					errors.push({ field: field.key });
				}
			}
		});

		if (errors.length) {
			const errs: FieldErrorsType = {};
			errors.forEach((err) => {
				errs[err.field as keyof FieldErrorsType] = true;
			});
			setFieldErrors(errs);
			return;
		}

		return true;
	}, [inputFields, fieldErrors, setFieldErrors]);

	useEffect(() => {
		validateFields();
	}, [inputFields]);

	// Check all required fields are entered
	const isNextEnabled = useMemo(() => {
		const values = Object.values(inputFields);
		const errors = Object.values(fieldErrors);

		for (let i = 0; i < values?.length; i++) {
			if (!values[i]) {
				return false;
			}
		}

		for (let i = 0; i < errors?.length; i++) {
			if (errors[i] === true) {
				return false;
			}
		}

		return true;
	}, [inputFields, fieldErrors]);

	const handleCardNumChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			form.setFieldsValue({ userId: initValues.userId });
			form.setFieldsValue({ capitaStarId: initValues.capitaStarId });
			setInputFields({ cardNumber: e.target?.value });
			dispatch(resetEnquiryDetails());
		},
		[inputFields, form, setInputFields]
	);

	const handleUserIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			form.setFieldsValue({ cardNumber: initValues.cardNumber });
			form.setFieldsValue({ capitaStarId: initValues.capitaStarId });
			setInputFields({ userId: e.target?.value });
			dispatch(resetEnquiryDetails());
		},
		[inputFields, form, setInputFields]
	);

	const showModal = () => {
		CountDownModal({
			timer: 3,
			errorIcon: true,
			title: Constants.ALREADY_DELINKED,
			onTimeComplete: () => {
				dispatch(resetEnquiryDetails());
			}
		});
	};

	const isAlreadyDelinked = (data: { capitastar_no: string; iu_no: string; kf_no: string }) => {
		if (data.capitastar_no == "" && pathname == Routes.DELINK) {
			showModal();
		}
		if (data.iu_no == "" && pathname == Routes.VEHICLE_DELINK) {
			showModal();
		}
		if (data.kf_no == "" && pathname == Routes.KRISFLYER_DELINK) {
			showModal();
		}
	};

	const handleMemberIdSearch = useCallback(async () => {
		if (inputFields.cardNumber) {
			const scannedCardNumber = inputFields.cardNumber?.slice(0, 19);
			const payload = {
				cr_card_number: scannedCardNumber,
				outlet_code: Constants.OUTLET_CODE
			};
			try {
				dispatch(resetEnquiryDetails());
				const { data } = await dispatch(getCardEnquiryDetails(payload)).unwrap();
				isAlreadyDelinked(data);
			} catch (error) {
				const err = error as any;
				if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
					CountDownModal({
						timer: 3,
						errorIcon: true,
						title: err?.response?.data?.errors?.[0]?.message,
						closable: true
					});
				}
			}
		}
		if (inputFields.userId) {
			const payload = {
				member_id: inputFields.userId
			};
			try {
				dispatch(resetEnquiryDetails());
				const { data } = await dispatch(getMemberEnquiryDetails(payload)).unwrap();
				isAlreadyDelinked(data);
			} catch (error) {
				const err = error as any;
				if (err?.response?.data?.errors && err?.response?.data?.errors?.length > 0) {
					CountDownModal({
						timer: 3,
						errorIcon: true,
						title: err?.response?.data?.errors?.[0]?.message,
						closable: true
					});
				}
			}
		}
	}, [inputFields, form, isNextEnabled]);

	const resetForm = () => {
		setInputFields({
			cardNumber: initValues.cardNumber,
			userId: initValues.userId
		});
		form?.setFieldsValue(initValues);
	};

	const handleSubmit = useCallback(async () => {
		await submitUtilHandler(inputFields.cardNumber, inputFields.userId, resetForm);
	}, [inputFields, form, submitUtilHandler, resetForm]);

	const handleVehicleIUSubmit = useCallback(async () => {
		await vehicleIUSubmitHandler(inputFields.cardNumber, inputFields.userId, resetForm);
	}, [inputFields, form, vehicleIUSubmitHandler, resetForm]);

	const handleKrisFlyerSubmit = useCallback(async () => {
		await KrisFlyerSubmitHandler(inputFields.cardNumber, inputFields.userId, resetForm);
	}, [inputFields, form, KrisFlyerSubmitHandler, resetForm]);

	const getFieldValue = (fieldName: string, fieldForm: FormInstance<any>) => {
		return Form.useWatch(fieldName, fieldForm);
	};

	return {
		inputFields,
		loading: cardEnquiryLoading === 1,
		memberloading: memberEnquiryLoading === 1,
		delinkloading: delinkMemberLoading === 1,
		capitaStarNumber,
		form,
		initValues,
		fieldErrors,
		isNextEnabled,
		vehicleIUNumber,
		KrisFlyerNumber,
		getFieldValue,
		handleCardNumChange,
		handleUserIdChange,
		handleMemberIdSearch,
		handleSubmit,
		handleVehicleIUSubmit,
		handleKrisFlyerSubmit
	};
}
